import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Confirm, FlexHorizontal, LoadMask, Spacer, useToasts } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import { Check } from '@material-ui/icons'
import InfiniteScroll from 'react-infinite-scroller'
import { ListTab } from '../components/ListTab'
import { TruncatedId } from '../../truncated-id'
import { tryAsync } from '../../../utils/tryAsync'
import { useDeleteNotificationSegment } from '../hooks/useDeleteNotificationSegment'
import { useHistory } from 'react-router'
import { useListNotificationSegments } from '../hooks/useListNotificationSegments'

export const ListSegments = () => {
  const history = useHistory()
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [deleteNotificationSegment, { loading: deleteInProgress }] = useDeleteNotificationSegment()
  const { addToast } = useToasts()

  const input = {
    cursor: null,
    limit: 25
  }

  const { loading, error, data, fetchMore, refetch } = useListNotificationSegments({
    input
  })

  return (
    <>
      <Confirm
        title='Delete Notification Segment? This cannot be undone!'
        handleClose={() => {
          setIsDeleting(null)
        }}
        open={!!isDeleting}
        handleAccept={async () => {
          if (!isDeleting) {
            return
          }

          setIsDeleting(null)

          const { result } = await tryAsync(() =>
            deleteNotificationSegment({
              variables: {
                input: {
                  id: isDeleting
                }
              }
            })
          )

          if (result && result.data?.deleteNotificationSegment.__typename === 'NotificationSegmentV2') {
            await refetch()
          } else {
            addToast('There was an error deleting your notification segment', {
              appearance: 'error',
              autoDismiss: true
            })
          }
        }}
      />
      <Box>
        <FlexHorizontal spaceBetween>
          <Typography variant='h4'>Notification Segments</Typography>
          <FlexHorizontal>
            <Button onClick={() => history.push('/notifications/segments/create')} variant='contained' color='primary'>
              Create Segment
            </Button>
          </FlexHorizontal>
        </FlexHorizontal>
        <Spacer size='extraLarge' />
        <ListTab activeTab='segments' />
        {loading && !data?.listNotificationSegments.notificationSegments ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity='error'>There was an error loading the notification segments</Alert>
        ) : !data?.listNotificationSegments.notificationSegments.length ? (
          <Alert severity='info'>There are no notification segments</Alert>
        ) : (
          <>
            {data ? (
              <InfiniteScroll
                pageStart={0}
                loadMore={async () => {
                  if (isLoadingMore) {
                    return
                  }

                  setIsLoadingMore(true)
                  return await fetchMore({
                    updateQuery(previousQueryResult, options) {
                      const newSearch = options.fetchMoreResult?.listNotificationSegments.notificationSegments || []
                      const previousSearch = previousQueryResult.listNotificationSegments.notificationSegments || []

                      return {
                        listNotificationSegments: {
                          ...previousQueryResult.listNotificationSegments,
                          nextCursor: options.fetchMoreResult?.listNotificationSegments.nextCursor || null,
                          notificationSegments: [...previousSearch, ...newSearch]
                        }
                      }
                    },
                    variables: {
                      input: {
                        ...input,
                        cursor: data.listNotificationSegments.nextCursor || null
                      }
                    }
                  }).then(res => {
                    setIsLoadingMore(false)
                    return res
                  })
                }}
                hasMore={!!data?.listNotificationSegments?.nextCursor}
                loader={
                  <Box>
                    <Spacer size='large' />
                    <CircularProgress />
                  </Box>
                }>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Dynamic</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.listNotificationSegments.notificationSegments.map(segment => (
                        <TableRow key={segment.id}>
                          <TableCell>
                            <Button
                              onClick={() => history.push(`/notifications/segments/${segment.id}`)}
                              variant='outlined'
                              color='primary'>
                              <TruncatedId value={segment.id} full />
                            </Button>
                          </TableCell>
                          <TableCell>{segment.title}</TableCell>
                          <TableCell>{segment.isDynamic ? <Check /> : null}</TableCell>
                          <TableCell>
                            <Button variant='outlined' color='secondary' onClick={() => setIsDeleting(segment.id)}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfiniteScroll>
            ) : null}
          </>
        )}
      </Box>
      {deleteInProgress ? <LoadMask /> : null}
    </>
  )
}
